"use client"

import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import {
  FolderIcon,
  TrashIcon,
  XIcon,
  LoaderIcon,
  PlusIcon,
  SearchIcon,
  RefreshCwIcon,
  AlertCircleIcon,
  CheckCircleIcon,
} from "lucide-react"
import apiClient from "../../utils/apiClient"
import { cleanAllOverlays } from "../../lib/overlay-cleaner"

// Toast notification component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose()
    }, 5000)
    return () => clearTimeout(timer)
  }, [onClose])

  return (
    <div
      className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 transition-all transform animate-slide-up ${
        type === "success"
          ? "bg-green-100 text-green-800 border-l-4 border-green-500"
          : "bg-red-100 text-red-800 border-l-4 border-red-500"
      }`}
    >
      {type === "success" ? <CheckCircleIcon size={20} /> : <AlertCircleIcon size={20} />}
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 text-gray-500 hover:text-gray-700">
        <XIcon size={16} />
      </button>
    </div>
  )
}

const EmptyState = ({ type, onNewProject }) => (
  <div className="flex flex-col items-center justify-center p-12 text-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
    {type === "projects" ? (
      <>
        <FolderIcon size={64} className="text-gray-400 mb-6" />
        <h3 className="text-xl font-semibold text-gray-700 mb-3">No Projects Yet</h3>
        <p className="text-gray-500 mb-6 max-w-md">Start your creative journey by creating your first project!</p>
        <button
          onClick={onNewProject}
          className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
        >
          <PlusIcon size={20} />
          Create New Project
        </button>
      </>
    ) : (
      <>
        <TrashIcon size={64} className="text-gray-400 mb-6" />
        <h3 className="text-xl font-semibold text-gray-700 mb-3">Trash is Empty</h3>
        <p className="text-gray-500 max-w-md">
          Projects you delete will appear here for 30 days before being permanently removed.
        </p>
      </>
    )}
  </div>
)

const MyStuff = ({ onClose, onNewProject, onLoadProject }) => {
  const [activeTab, setActiveTab] = useState("projects")
  const [projects, setProjects] = useState([])
  const [trashedProjects, setTrashedProjects] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [toast, setToast] = useState({ show: false, message: "", type: "success" })
  const [selectedProject, setSelectedProject] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Validate props
  useEffect(() => {
    if (typeof onLoadProject !== "function") {
      console.error("onLoadProject must be a function")
    }
  }, [onLoadProject])

  useEffect(() => {
    if (activeTab === "projects") {
      fetchProjects()
    } else {
      fetchTrashedProjects()
    }
  }, [activeTab])

  // Add mounted ref for cleanup
  const mounted = React.useRef(true)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mounted.current = false
    }
  }, [])

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type })
  }

  const hideToast = () => {
    setToast({ ...toast, show: false })
  }

  const fetchProjects = async () => {
    if (!mounted.current) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient.get("/api/projects/user-projects", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      })

      if (!mounted.current) return

      if (response.data.success) {
        // Ensure we have all required fields
        const formattedProjects = response.data.projects.map((project) => ({
          _id: project._id,
          title: project.title || "Untitled Project",
          createdAt: project.createdAt,
          updatedAt: project.updatedAt,
        }))
        setProjects(formattedProjects)
      } else {
        throw new Error(response.data.message || "Failed to fetch projects")
      }
    } catch (error) {
      console.error("Error fetching projects:", error)
      // Only update error state if still mounted
      if (mounted.current) {
        setError(error.message || "Failed to load projects")
        setProjects([])
      }
    } finally {
      if (mounted.current) {
        setIsLoading(false)
      }
    }
  }

  const fetchTrashedProjects = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const userId = localStorage.getItem("userId")
      if (!userId) {
        throw new Error("User not authenticated")
      }

      const response = await apiClient.get("/api/projects/trash", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      })

      if (response.data.success) {
        setTrashedProjects(response.data.projects)
      } else {
        throw new Error(response.data.message || "Failed to fetch trash")
      }
    } catch (error) {
      console.error("Error fetching trash:", error)
      setError(error.message || "Failed to load trash")
      setTrashedProjects([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleLoadProject = async (projectId) => {
    try {
      cleanAllOverlays();
      setSelectedProject(projectId)
      setIsLoading(true)
      setError(null)

      if (typeof onLoadProject !== "function") {
        throw new Error("Project loading function not provided")
      }

      const response = await apiClient.get(`/api/projects/${projectId}/download`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        responseType: "blob",
      })

      const targetProject = projects.find((p) => p._id === projectId);
      const projectTitle = targetProject ? targetProject.title : "";

      const blob = new Blob([response.data], { type: "application/x.scratch.sb3" });
      const arrayBuffer = await blob.arrayBuffer();

      cleanAllOverlays();
      await onLoadProject(arrayBuffer, projectTitle, projectId);
      cleanAllOverlays();

      showToast("Project loaded successfully!", "success");

      // Only close if still mounted
      if (mounted.current) {
        onClose();
      }
    } catch (error) {
      console.error("Error downloading project:", error)
      // Only update error state if still mounted
      if (mounted.current) {
        setError(error.message || "Failed to download project")
        showToast("Failed to load project", "error")
      }
    } finally {
      // Only update loading state if still mounted
      if (mounted.current) {
        setIsLoading(false)
        setSelectedProject(null)
      }
    }
  }

  const handleMoveToTrash = async (projectId) => {
    try {
      setSelectedProject(projectId)
      const token = localStorage.getItem("accessToken")
      if (!token) {
        throw new Error("User not authenticated")
      }

      const response = await apiClient.post(
        `/api/projects/${projectId}/trash`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )

      if (response.data.success) {
        await fetchProjects() // Refresh the projects list
        showToast("Project moved to trash", "success")
      } else {
        throw new Error(response.data.message || "Failed to move project to trash")
      }
    } catch (error) {
      console.error("Error moving to trash:", error)
      setError(error.response?.data?.message || "Failed to move project to trash")
      showToast("Failed to move project to trash", "error")
    } finally {
      setSelectedProject(null)
    }
  }

  const handleRestoreProject = async (projectId) => {
    try {
      setSelectedProject(projectId)
      const token = localStorage.getItem("accessToken")
      if (!token) {
        throw new Error("User not authenticated")
      }

      const response = await apiClient.post(
        `/api/projects/${projectId}/restore`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      )

      if (response.data.success) {
        await fetchTrashedProjects() // Refresh trash list
        showToast("Project restored successfully", "success")
      } else {
        throw new Error(response.data.message || "Failed to restore project")
      }
    } catch (error) {
      console.error("Error restoring project:", error)
      setError(error.response?.data?.message || "Failed to restore project")
      showToast("Failed to restore project", "error")
    } finally {
      setSelectedProject(null)
    }
  }

  const handleDeletePermanently = async (projectId) => {
    try {
      setSelectedProject(projectId)
      const token = localStorage.getItem("accessToken")
      if (!token) {
        throw new Error("User not authenticated")
      }

      const response = await apiClient.delete(`/api/projects/${projectId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.data.success) {
        await fetchTrashedProjects() // Refresh the trashed projects list
        showToast("Project deleted permanently", "success")
      } else {
        throw new Error(response.data.message || "Failed to delete project")
      }
    } catch (error) {
      console.error("Error deleting project:", error)
      setError(error.response?.data?.message || "Failed to delete project permanently")
      showToast("Failed to delete project", "error")
    } finally {
      setSelectedProject(null)
    }
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    if (activeTab === "projects") {
      await fetchProjects()
    } else {
      await fetchTrashedProjects()
    }
    setIsRefreshing(false)
  }

  // Filter projects based on search term
  const filteredProjects =
    activeTab === "projects"
      ? projects.filter((project) => project.title.toLowerCase().includes(searchTerm.toLowerCase()))
      : trashedProjects.filter((project) => project.title.toLowerCase().includes(searchTerm.toLowerCase()))

  // Format date to be more readable with seconds included
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date)
  }

  // Update the project card rendering to include more details
  const renderProjectCard = (project) => (
    <div
      key={project._id}
      className={`border rounded-lg overflow-hidden transition-all duration-300 ${
        selectedProject === project._id
          ? "ring-2 ring-blue-500 shadow-lg transform scale-[1.02]"
          : "hover:shadow-lg hover:border-blue-200"
      }`}
    >
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b">
        <h3 className="font-semibold text-lg mb-1 text-gray-800 truncate">{project.title || "Untitled Project"}</h3>
      </div>
      <div className="p-4">
        <div className="space-y-2 mb-4">
          <p className="text-sm text-gray-500 flex items-center gap-1">
            <span className="font-medium">Modified:</span> {formatDate(project.updatedAt)}
          </p>
          <p className="text-sm text-gray-500 flex items-center gap-1">
            <span className="font-medium">Created:</span> {formatDate(project.createdAt)}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleLoadProject(project._id)}
            disabled={isLoading && selectedProject === project._id}
            className={`flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-md
              hover:from-blue-600 hover:to-blue-700 transition-colors flex items-center justify-center
              ${isLoading && selectedProject === project._id ? "opacity-70 cursor-not-allowed" : "shadow-sm hover:shadow"}`}
          >
            {isLoading && selectedProject === project._id ? (
              <>
                <LoaderIcon className="animate-spin mr-2" size={18} />
                Loading...
              </>
            ) : (
              "Open Project"
            )}
          </button>
          <button
            onClick={() => handleMoveToTrash(project._id)}
            disabled={isLoading && selectedProject === project._id}
            className="p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 rounded-md transition-colors"
            title="Move to Trash"
          >
            <TrashIcon size={20} />
          </button>
        </div>
      </div>
    </div>
  )

  const renderTrashedProjects = (project) => (
    <div
      key={project._id}
      className={`border rounded-lg overflow-hidden transition-all duration-300 ${
        selectedProject === project._id
          ? "ring-2 ring-blue-500 shadow-lg transform scale-[1.02]"
          : "hover:shadow-lg hover:border-blue-200"
      }`}
    >
      <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 border-b">
        <h3 className="font-semibold text-lg mb-1 text-gray-800 truncate">{project.title || "Untitled Project"}</h3>
      </div>
      <div className="p-4">
        <div className="space-y-2 mb-4">
          {/* <p className="text-sm text-gray-500 flex items-center gap-1">
            <span className="font-medium">Deleted:</span>{" "}
            {project.deletedAt ? formatDate(project.deletedAt) : "Unknown"}
          </p> */}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleRestoreProject(project._id)}
            disabled={selectedProject === project._id}
            className={`flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-md
            hover:from-green-600 hover:to-green-700 transition-colors shadow-sm hover:shadow ${
              selectedProject === project._id ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {selectedProject === project._id ? <LoaderIcon className="animate-spin mx-auto" size={18} /> : "Restore"}
          </button>
          <button
            onClick={() => handleDeletePermanently(project._id)}
            disabled={selectedProject === project._id}
            className={`flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-md
            hover:from-red-600 hover:to-red-700 transition-colors shadow-sm hover:shadow ${
              selectedProject === project._id ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {selectedProject === project._id ? (
              <LoaderIcon className="animate-spin mx-auto" size={18} />
            ) : (
              "Delete Forever"
            )}
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-xl w-11/12 max-w-6xl h-5/6 overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <FolderIcon className="mr-2 text-blue-500" size={24} />
            My Projects
          </h1>
          <div className="flex gap-4">
            <button
              onClick={onNewProject}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <PlusIcon size={20} />
              New Project
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              aria-label="Close"
            >
              <XIcon size={24} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Tabs and Search */}
        <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-gray-200 bg-white px-6 py-2">
          <div className="flex">
            <button
              className={`flex items-center gap-2 px-6 py-3 transition-colors ${
                activeTab === "projects"
                  ? "border-b-2 border-blue-500 text-blue-600 font-medium"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              onClick={() => setActiveTab("projects")}
            >
              <FolderIcon size={20} />
              My Projects
            </button>
            <button
              className={`flex items-center gap-2 px-6 py-3 transition-colors ${
                activeTab === "trash"
                  ? "border-b-2 border-blue-500 text-blue-600 font-medium"
                  : "text-gray-600 hover:text-gray-800"
              }`}
              onClick={() => setActiveTab("trash")}
            >
              <TrashIcon size={20} />
              Trash
            </button>
          </div>

          <div className="flex items-center gap-2 mt-2 md:mt-0">
            <div className="relative">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-64 text-gray-800"
              />
              <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XIcon size={16} />
                </button>
              )}
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`p-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors ${isRefreshing ? "opacity-50" : ""}`}
              title="Refresh"
            >
              <RefreshCwIcon size={18} className={`text-gray-600 ${isRefreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="p-4 m-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-md flex items-center">
            <AlertCircleIcon size={20} className="mr-2" />
            {error}
          </div>
        )}

        {/* Loading spinner */}
        {isLoading && !selectedProject && (
          <div className="flex-1 flex justify-center items-center p-8">
            <div className="flex flex-col items-center">
              <LoaderIcon size={40} className="animate-spin text-blue-500 mb-4" />
              <p className="text-gray-600">Loading your projects...</p>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {!isLoading &&
            !error &&
            (activeTab === "projects" ? (
              filteredProjects.length === 0 ? (
                searchTerm ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No projects match your search</p>
                  </div>
                ) : (
                  <EmptyState type="projects" onNewProject={onNewProject} />
                )
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProjects.map((project) => renderProjectCard(project))}
                </div>
              )
            ) : filteredProjects.length === 0 ? (
              searchTerm ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No trashed projects match your search</p>
                </div>
              ) : (
                <EmptyState type="trash" />
              )
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map((project) => renderTrashedProjects(project))}
              </div>
            ))}
        </div>
      </div>

      {/* Toast notification */}
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
    </div>
  )
}

MyStuff.propTypes = {
  onClose: PropTypes.func.isRequired,
  onNewProject: PropTypes.func.isRequired,
  onLoadProject: PropTypes.func.isRequired,
}

// Add default props to prevent undefined function calls
MyStuff.defaultProps = {
  onClose: () => console.warn("onClose not provided"),
  onNewProject: () => console.warn("onNewProject not provided"),
  onLoadProject: () => console.warn("onLoadProject not provided"),
}

export default MyStuff
